import {
  AcceptAddonOrder,
  AddonOrderToAccept,
  FreshServiceProposal,
  ProposalItemSource,
  ServiceAction,
  ServiceProposalDependencies,
  ServiceProposalPartAction,
  SubmitFreshServiceProposal,
  Translator,
} from "@repo/common"
import { serviceActionMapper } from "./service-action-mapper"

// "Byt kedja": the requiring action, as it reads on its own.
const actionWithPart = (
  action: ServiceAction,
  partName: string,
  t: Translator,
): string =>
  `${t(serviceActionMapper(action).labelKey)} ${partName.toLowerCase()}`

// "byte kedja": what a requirement needs, as it reads inside a sentence.
const requiredActionWithPart = (
  action: ServiceAction,
  partName: string,
  t: Translator,
): string =>
  `${t(serviceActionMapper(action).nounKey).toLowerCase()} ${partName.toLowerCase()}`

export interface DependencyItem {
  key: string
  requirer: string
  required: string
}

export function getDependencyItems(
  data: ServiceProposalDependencies,
  t: Translator,
): DependencyItem[] {
  const dependencies: DependencyItem[] = []

  // Part action dependencies
  for (const action of data.partActionsWithDependencies) {
    const requirer = actionWithPart(action.action, action.partName, t)

    // Required part actions
    for (const dependency of action.requiredPartActions) {
      dependencies.push({
        key: `${action.partId}-component-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required manual actions
    for (const dependency of action.requiredManualServiceActions) {
      dependencies.push({
        key: `${action.partId}-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }

    // Required wash
    if (action.requiredWash) {
      dependencies.push({
        key: `${action.partId}-wash-${action.requiredWash.originalWashId}`,
        requirer,
        required: action.requiredWash.name.toLowerCase(),
      })
    }
  }

  // Manual action dependencies
  for (const action of data.manualActionsWithDependencies) {
    const requirer = action.title

    // Required manual actions
    for (const dependency of action.requiredManualServiceActions) {
      dependencies.push({
        key: `${action.id}-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }

    // Required part actions
    for (const dependency of action.requiredPartActions) {
      dependencies.push({
        key: `${action.id}-component-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required wash
    if (action.requiredWash) {
      dependencies.push({
        key: `${action.id}-wash-${action.requiredWash.originalWashId}`,
        requirer,
        required: action.requiredWash.name.toLowerCase(),
      })
    }
  }

  // Wash dependencies
  if (data.washWithDependencies) {
    const requirer = data.washWithDependencies.name

    // Required part actions
    for (const dependency of data.washWithDependencies.requiredPartActions) {
      dependencies.push({
        key: `wash-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required manual actions
    for (const dependency of data.washWithDependencies
      .requiredManualServiceActions) {
      dependencies.push({
        key: `wash-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }
  }

  return dependencies
}

export function getFilteredDependencyItems(
  data: ServiceProposalDependencies,
  proposal: FreshServiceProposal,
  formValues: SubmitFreshServiceProposal,
  t: Translator,
): DependencyItem[] {
  const dependencies: DependencyItem[] = []

  // Get all selected action IDs for filtering
  const selectedPartActionIds = new Set(
    formValues.selectedPartActions.map((pa) => pa.partId),
  )
  const selectedManualActionIds = new Set(
    formValues.selectedManualActions.map((ma) => ma.id),
  )

  // Add selected part actions from packages
  for (const selectedPackage of formValues.selectedPackages) {
    const packageData = proposal.packages.find(
      (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
    )
    if (packageData) {
      for (const selectedPartAction of selectedPackage.selectedPartActions) {
        selectedPartActionIds.add(selectedPartAction.partId)
      }
    }
  }

  const isWashSelected = Boolean(formValues.selectedWashOriginalId)

  // Part action dependencies - only show if the requirer action is selected
  for (const action of data.partActionsWithDependencies) {
    // Check if this part action is actually selected
    if (!selectedPartActionIds.has(action.partId)) {
      continue
    }

    const requirer = actionWithPart(action.action, action.partName, t)

    // Required part actions
    for (const dependency of action.requiredPartActions) {
      dependencies.push({
        key: `${action.partId}-component-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required manual actions
    for (const dependency of action.requiredManualServiceActions) {
      dependencies.push({
        key: `${action.partId}-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }

    // Required wash
    if (action.requiredWash) {
      dependencies.push({
        key: `${action.partId}-wash-${action.requiredWash.originalWashId}`,
        requirer,
        required: action.requiredWash.name.toLowerCase(),
      })
    }
  }

  // Manual action dependencies - only show if the requirer action is selected
  for (const action of data.manualActionsWithDependencies) {
    // Check if this manual action is actually selected
    if (!selectedManualActionIds.has(action.id)) {
      continue
    }

    const requirer = action.title

    // Required manual actions
    for (const dependency of action.requiredManualServiceActions) {
      dependencies.push({
        key: `${action.id}-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }

    // Required part actions
    for (const dependency of action.requiredPartActions) {
      dependencies.push({
        key: `${action.id}-component-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required wash
    if (action.requiredWash) {
      dependencies.push({
        key: `${action.id}-wash-${action.requiredWash.originalWashId}`,
        requirer,
        required: action.requiredWash.name.toLowerCase(),
      })
    }
  }

  // Wash dependencies - only show if wash is selected
  if (data.washWithDependencies && isWashSelected) {
    const requirer = data.washWithDependencies.name

    // Required part actions
    for (const dependency of data.washWithDependencies.requiredPartActions) {
      dependencies.push({
        key: `wash-${dependency.partId}`,
        requirer,
        required: requiredActionWithPart(
          dependency.action,
          dependency.partName,
          t,
        ),
      })
    }

    // Required manual actions
    for (const dependency of data.washWithDependencies
      .requiredManualServiceActions) {
      dependencies.push({
        key: `wash-manual-${dependency.id}`,
        requirer,
        required: dependency.title.toLowerCase(),
      })
    }
  }

  return dependencies
}

export function getViolatedDependencies(
  proposal: FreshServiceProposal,
  value: SubmitFreshServiceProposal,
): Set<string> {
  const selectedPartActionIds = new Set(
    value.selectedPartActions.map((pa) => pa.partId),
  )
  const selectedManualActionIds = new Set(
    value.selectedManualActions.map((ma) => ma.id),
  )

  // Add selected part actions from packages
  for (const selectedPackage of value.selectedPackages) {
    const packageData = proposal.packages.find(
      (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
    )
    if (packageData) {
      for (const selectedPartAction of selectedPackage.selectedPartActions) {
        selectedPartActionIds.add(selectedPartAction.partId)
      }
    }
  }

  // Collect wash selections from both standalone and packages
  let selectedWashOriginalId = value.selectedWashOriginalId
  for (const selectedPackage of value.selectedPackages) {
    if (selectedPackage.selectedWashOriginalId) {
      selectedWashOriginalId = selectedPackage.selectedWashOriginalId
      break
    }
  }

  const violatedDependencies = new Set<string>()

  // Check part action dependencies for standalone actions
  for (const selectedAction of value.selectedPartActions) {
    const actionData = proposal.partActions.find(
      (a) => a.partId === selectedAction.partId,
    )
    if (actionData) {
      // Check required part actions
      for (const requiredAction of actionData.requiredPartActions || []) {
        if (!selectedPartActionIds.has(requiredAction.partId)) {
          violatedDependencies.add(
            `${actionData.partId}-component-${requiredAction.partId}`,
          )
        }
      }
      // Check required manual actions
      for (const requiredManual of actionData.requiredManualServiceActions ||
        []) {
        if (!selectedManualActionIds.has(requiredManual.id)) {
          violatedDependencies.add(
            `${actionData.partId}-manual-${requiredManual.id}`,
          )
        }
      }
      // Check required wash
      if (
        actionData.requiredWash &&
        selectedWashOriginalId !== actionData.requiredWash.originalWashId
      ) {
        violatedDependencies.add(
          `${actionData.partId}-wash-${actionData.requiredWash.originalWashId}`,
        )
      }
    }
  }

  // Check part action dependencies for actions inside packages
  for (const selectedPackage of value.selectedPackages) {
    const packageData = proposal.packages.find(
      (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
    )
    if (packageData) {
      for (const selectedPartAction of selectedPackage.selectedPartActions) {
        const actionData = packageData.partActions.find(
          (a) => a.partId === selectedPartAction.partId,
        )
        if (actionData) {
          // Check required part actions
          for (const requiredAction of actionData.requiredPartActions || []) {
            if (!selectedPartActionIds.has(requiredAction.partId)) {
              violatedDependencies.add(
                `${actionData.partId}-component-${requiredAction.partId}`,
              )
            }
          }
          // Check required manual actions
          for (const requiredManual of actionData.requiredManualServiceActions ||
            []) {
            if (!selectedManualActionIds.has(requiredManual.id)) {
              violatedDependencies.add(
                `${actionData.partId}-manual-${requiredManual.id}`,
              )
            }
          }
          // Check required wash
          if (
            actionData.requiredWash &&
            selectedWashOriginalId !== actionData.requiredWash.originalWashId
          ) {
            violatedDependencies.add(
              `${actionData.partId}-wash-${actionData.requiredWash.originalWashId}`,
            )
          }
        }
      }
    }
  }

  // Check manual action dependencies
  for (const selectedManual of value.selectedManualActions) {
    const manualData = proposal.manualActions.find(
      (m) => m.id === selectedManual.id,
    )
    if (manualData) {
      // Check required part actions
      for (const requiredAction of manualData.requiredPartActions || []) {
        if (!selectedPartActionIds.has(requiredAction.partId)) {
          violatedDependencies.add(
            `${manualData.id}-component-${requiredAction.partId}`,
          )
        }
      }
      // Check required manual actions
      for (const requiredManual of manualData.requiredManualServiceActions ||
        []) {
        if (!selectedManualActionIds.has(requiredManual.id)) {
          violatedDependencies.add(
            `${manualData.id}-manual-${requiredManual.id}`,
          )
        }
      }
      // Check required wash
      if (
        manualData.requiredWash &&
        selectedWashOriginalId !== manualData.requiredWash.originalWashId
      ) {
        violatedDependencies.add(
          `${manualData.id}-wash-${manualData.requiredWash.originalWashId}`,
        )
      }
    }
  }

  if (selectedWashOriginalId && proposal.wash) {
    // Check required part actions for wash
    for (const requiredAction of proposal.wash.requiredPartActions || []) {
      if (!selectedPartActionIds.has(requiredAction.partId)) {
        violatedDependencies.add(`wash-${requiredAction.partId}`)
      }
    }
    // Check required manual actions for wash
    for (const requiredManual of proposal.wash.requiredManualServiceActions ||
      []) {
      if (!selectedManualActionIds.has(requiredManual.id)) {
        violatedDependencies.add(`wash-manual-${requiredManual.id}`)
      }
    }
  }

  return violatedDependencies
}

/**
 * Addon-order twin of {@link getViolatedDependencies}. Works off the addon
 * proposal's `dependencies` block and the customer's `AcceptAddonOrder`
 * selection, producing the SAME violation keys that `getDependencyItems`
 * emits for `proposal.dependencies` so the shared `ServiceProposalDependencies`
 * card can highlight/vibrate the matching rows.
 */
/**
 * The work an addon order's selection is missing: for every "Beror på" link the
 * customer has broken, the name of what the selected item needs. Used to tell
 * them what to add rather than that something, somewhere, is wrong.
 */
export function getMissingDependencyLabels(
  proposal: AddonOrderToAccept,
  value: AcceptAddonOrder,
  t: Translator,
): string[] {
  const violated = getViolatedAddonDependencies(proposal, value)
  if (violated.size === 0) return []

  const labels = new Set<string>()
  for (const item of getDependencyItems(proposal.dependencies, t)) {
    if (violated.has(item.key)) labels.add(item.required)
  }
  return [...labels]
}

export function getViolatedAddonDependencies(
  proposal: AddonOrderToAccept,
  value: AcceptAddonOrder,
): Set<string> {
  const selectedPartIds = new Set(value.acceptedPartIds)
  const selectedManualIds = new Set(value.acceptedManualServiceActionIds)

  // Accepting a package implicitly selects its part-actions.
  const acceptedPackageIds = new Set(value.acceptedPackageIds ?? [])
  for (const pkg of proposal.packages) {
    if (!acceptedPackageIds.has(pkg.id)) continue
    for (const action of pkg.actions) {
      selectedPartIds.add(action.partId)
    }
  }

  const selectedWashOriginalId = value.selectedWashOriginalId ?? null

  const violated = new Set<string>()
  const { dependencies } = proposal

  // Part-action subjects — only when the requirer is actually selected.
  for (const part of dependencies.partActionsWithDependencies) {
    if (!selectedPartIds.has(part.partId)) continue
    for (const req of part.requiredPartActions) {
      if (!selectedPartIds.has(req.partId)) {
        violated.add(`${part.partId}-component-${req.partId}`)
      }
    }
    for (const req of part.requiredManualServiceActions) {
      if (!selectedManualIds.has(req.id)) {
        violated.add(`${part.partId}-manual-${req.id}`)
      }
    }
    if (
      part.requiredWash &&
      selectedWashOriginalId !== part.requiredWash.originalWashId
    ) {
      violated.add(`${part.partId}-wash-${part.requiredWash.originalWashId}`)
    }
  }

  // Manual-action subjects.
  for (const manual of dependencies.manualActionsWithDependencies) {
    if (!selectedManualIds.has(manual.id)) continue
    for (const req of manual.requiredManualServiceActions) {
      if (!selectedManualIds.has(req.id)) {
        violated.add(`${manual.id}-manual-${req.id}`)
      }
    }
    for (const req of manual.requiredPartActions) {
      if (!selectedPartIds.has(req.partId)) {
        violated.add(`${manual.id}-component-${req.partId}`)
      }
    }
    if (
      manual.requiredWash &&
      selectedWashOriginalId !== manual.requiredWash.originalWashId
    ) {
      violated.add(`${manual.id}-wash-${manual.requiredWash.originalWashId}`)
    }
  }

  // Wash subject — only when a wash is selected.
  if (selectedWashOriginalId && dependencies.washWithDependencies) {
    for (const req of dependencies.washWithDependencies.requiredPartActions) {
      if (!selectedPartIds.has(req.partId)) {
        violated.add(`wash-${req.partId}`)
      }
    }
    for (const req of dependencies.washWithDependencies
      .requiredManualServiceActions) {
      if (!selectedManualIds.has(req.id)) {
        violated.add(`wash-manual-${req.id}`)
      }
    }
  }

  return violated
}

/**
 * A single thing the customer can select or deselect in the proposal. Used by
 * the redesigned screen to reason about dependencies without caring whether the
 * item is a part action, a manual action or the wash.
 */
export interface ProposalSelectionRef {
  kind: "PART" | "MANUAL" | "WASH"
  /** partId, manual action id, or originalWashId. */
  id: string
  label: string
}

const refKey = (ref: ProposalSelectionRef): string => `${ref.kind}:${ref.id}`

const partActionLabel = (
  action: ServiceProposalPartAction,
  t: Translator,
): string => actionWithPart(action.action, action.partName, t)

/** Every part action in the source, whether standalone or inside a package. */
const collectAllPartActions = (
  source: ProposalItemSource,
): ServiceProposalPartAction[] => [
  ...source.partActions,
  ...source.packages.flatMap((servicePackage) => servicePackage.partActions),
]

interface DependencyLink {
  requirement: ProposalSelectionRef
  dependent: ProposalSelectionRef
}

/** Every "this needs that" across everything on offer. */
const collectDependencyLinks = (
  source: ProposalItemSource,
  t: Translator,
): DependencyLink[] => {
  const links: DependencyLink[] = []

  const link = (
    requirement: ProposalSelectionRef,
    dependent: ProposalSelectionRef,
  ): void => {
    links.push({ requirement, dependent })
  }

  for (const action of collectAllPartActions(source)) {
    const dependent: ProposalSelectionRef = {
      kind: "PART",
      id: action.partId,
      label: partActionLabel(action, t),
    }
    for (const required of action.requiredPartActions) {
      link(
        {
          kind: "PART",
          id: required.partId,
          label: actionWithPart(required.action, required.partName, t),
        },
        dependent,
      )
    }
    for (const required of action.requiredManualServiceActions) {
      link(
        { kind: "MANUAL", id: required.id, label: required.title },
        dependent,
      )
    }
    if (action.requiredWash) {
      link(
        {
          kind: "WASH",
          id: action.requiredWash.originalWashId,
          label: action.requiredWash.name,
        },
        dependent,
      )
    }
  }

  for (const action of source.manualActions) {
    const dependent: ProposalSelectionRef = {
      kind: "MANUAL",
      id: action.id,
      label: action.title,
    }
    for (const required of action.requiredPartActions) {
      link(
        {
          kind: "PART",
          id: required.partId,
          label: actionWithPart(required.action, required.partName, t),
        },
        dependent,
      )
    }
    for (const required of action.requiredManualServiceActions) {
      link(
        { kind: "MANUAL", id: required.id, label: required.title },
        dependent,
      )
    }
    if (action.requiredWash) {
      link(
        {
          kind: "WASH",
          id: action.requiredWash.originalWashId,
          label: action.requiredWash.name,
        },
        dependent,
      )
    }
  }

  // A wash is a dependent as well as a requirement: removing a part the wash
  // needs leaves the wash dangling just as it would any other item.
  const washes = source.washAlternatives
    ? source.washAlternatives.alternatives
    : source.wash
      ? [source.wash]
      : []
  for (const wash of washes) {
    const dependent: ProposalSelectionRef = {
      kind: "WASH",
      id: wash.originalWashId,
      label: wash.name,
    }
    for (const required of wash.requiredPartActions) {
      link(
        {
          kind: "PART",
          id: required.partId,
          label: actionWithPart(required.action, required.partName, t),
        },
        dependent,
      )
    }
    for (const required of wash.requiredManualServiceActions) {
      link(
        { kind: "MANUAL", id: required.id, label: required.title },
        dependent,
      )
    }
  }

  return links
}

const groupLinks = (
  links: DependencyLink[],
  keyOf: (link: DependencyLink) => ProposalSelectionRef,
  entryOf: (link: DependencyLink) => ProposalSelectionRef,
): Map<string, ProposalSelectionRef[]> => {
  const index = new Map<string, ProposalSelectionRef[]>()
  for (const entry of links) {
    const key = refKey(keyOf(entry))
    const existing = index.get(key)
    if (existing) {
      existing.push(entryOf(entry))
    } else {
      index.set(key, [entryOf(entry)])
    }
  }
  return index
}

/**
 * For each requirement, the items that would break if the requirement went
 * away.
 */
const buildDependentIndex = (
  source: ProposalItemSource,
  t: Translator,
): Map<string, ProposalSelectionRef[]> =>
  groupLinks(
    collectDependencyLinks(source, t),
    (entry) => entry.requirement,
    (entry) => entry.dependent,
  )

/** For each item, what it needs. */
const buildRequirementIndex = (
  source: ProposalItemSource,
  t: Translator,
): Map<string, ProposalSelectionRef[]> =>
  groupLinks(
    collectDependencyLinks(source, t),
    (entry) => entry.dependent,
    (entry) => entry.requirement,
  )

/** Follows `index` from `target`, collecting what `isIncluded` lets through. */
const walkFrom = (
  index: Map<string, ProposalSelectionRef[]>,
  target: ProposalSelectionRef,
  isIncluded: (key: string) => boolean,
): ProposalSelectionRef[] => {
  const found = new Map<string, ProposalSelectionRef>()
  const queue: ProposalSelectionRef[] = [target]
  const visited = new Set<string>([refKey(target)])

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) break

    for (const next of index.get(refKey(current)) ?? []) {
      const key = refKey(next)
      if (visited.has(key)) continue
      visited.add(key)
      if (!isIncluded(key)) continue
      found.set(key, next)
      queue.push(next)
    }
  }

  return [...found.values()]
}

/** The selected-item keys `getDependentSelections` matches against. */
export const collectSelectedKeys = (
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const keys = new Set<string>()
  for (const selected of values.selectedPartActions) {
    keys.add(`PART:${selected.partId}`)
  }
  for (const selectedPackage of values.selectedPackages) {
    for (const selected of selectedPackage.selectedPartActions) {
      keys.add(`PART:${selected.partId}`)
    }
    if (selectedPackage.selectedWashOriginalId) {
      keys.add(`WASH:${selectedPackage.selectedWashOriginalId}`)
    }
  }
  for (const selected of values.selectedManualActions) {
    keys.add(`MANUAL:${selected.id}`)
  }
  if (values.selectedWashOriginalId) {
    keys.add(`WASH:${values.selectedWashOriginalId}`)
  }
  return keys
}

/**
 * Everything currently selected that would be left dangling if `target` were
 * deselected, following the dependency chain transitively. Drives the
 * "Andra åtgärder kräver denna" confirmation sheet.
 */
export function getDependentSelections(
  source: ProposalItemSource,
  selectedKeys: ReadonlySet<string>,
  target: ProposalSelectionRef,
  t: Translator,
): ProposalSelectionRef[] {
  return walkFrom(buildDependentIndex(source, t), target, (key) =>
    selectedKeys.has(key),
  )
}

/**
 * Everything `target` needs that isn't selected, following the requirement
 * chain transitively. Drives the "Denna åtgärd kräver andra" confirmation:
 * adding work back must bring what it depends on with it.
 */
export function getMissingRequirements(
  source: ProposalItemSource,
  selectedKeys: ReadonlySet<string>,
  target: ProposalSelectionRef,
  t: Translator,
): ProposalSelectionRef[] {
  return walkFrom(
    buildRequirementIndex(source, t),
    target,
    (key) => !selectedKeys.has(key),
  )
}
